import * as React from 'react';
import { FormPanel, SubmitButton, Select, Spinner, Alert, Panel, Switch, Modal } from '@ixicare/ui';
import { t } from '~/i18n';
import { getDefaultStateReducer } from '~/utils/reducer';
import { DeviceType, Profile, ZoneEntry } from '@ixicare/resources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStores } from '~store/utils';
import { UserAction } from '~config/notification';
import { OutdoorGeofencingMode, ProfileConfiguration } from '@ixicare/resources';
import GeofenceCreatorContainer from '~modules/geofencing/container/GeofenceCreatorContainer';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { useZones } from '~hooks/zone';
import GeoJSON from 'geojson';

export function useProfileConfiguration(profileId: number) {
  const { notifications } = useStores();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['profileConfiguration', profileId],
    queryFn: () => Profile.getProfileConfiguration(profileId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const patchConfigMutation = useMutation({
    mutationFn: (body: ProfileConfiguration) => Profile.patchProfileConfiguration(profileId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(['profileConfiguration'], data);
      notifications.addUserActionNotification({
        type: UserAction.ProfileConfigurationUpdate,
        notificationType: 'success',
        message: t('profile-configuration.update.success'),
      });
    },
  });

  return {
    mutate: patchConfigMutation.mutate,
    isMutating: patchConfigMutation.isPending,
    isLoading: query.isLoading,
    data: query.data,
  };
}

export const ResidentConfigurationForm: React.FC<{ profile: UserProfileEntry }> = ({ profile }) => {
  const query = useProfileConfiguration(profile.id);

  return (
    <Panel.Section>
      <Panel.Title>{t('profile-configuration.title')}</Panel.Title>
      <FormPanel>
        {!query.isLoading ? (
          <Form
            profile={profile}
            isSubmitting={query.isMutating}
            onSubmit={query.mutate}
            configuration={query.data}
            siteUuid={profile.site?.uuid}
          />
        ) : (
          <Spinner />
        )}
      </FormPanel>
    </Panel.Section>
  );
};

const checkinFrequencies = [15, 30, 60, 90, 2 * 60, 5 * 60, 10 * 60, 30 * 60, 60 * 60];
const getCheckinLabel = (seconds: number) => {
  if (seconds < 60) return `${seconds} sec`;
  return `${seconds / 60} min`;
};

function Form(props: {
  onSubmit: (data: ProfileConfiguration) => void;
  configuration?: ProfileConfiguration;
  profile: UserProfileEntry;
  isSubmitting: boolean;
  siteUuid?: string;
}) {
  const [outdoorGeo, setOutdoorGeo] = React.useState<GeoJSON.Feature<GeoJSON.Polygon> | undefined>(
    props.configuration?.['outdoor-geofencing-geo']
  );
  delete props.configuration?.['outdoor-geofencing-geo'];
  const [state, dispatch] = React.useReducer(getDefaultStateReducer<ProfileConfiguration>(), props.configuration ?? {});

  const handleSubmit = React.useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      const data: ProfileConfiguration = {
        ...state,
        'outdoor-geofencing-geo': outdoorGeo,
      };
      props.onSubmit(data);
    },
    [state, outdoorGeo]
  );

  const isProDevice = props.profile.ownedDevice?.deviceType === DeviceType.WATCHERR;
  const outdoorGeofencingMode = state['outdoor-geofencing-mode'] ?? OutdoorGeofencingMode.None;

  const handleOutdoorGeofencingModeChange = (value: OutdoorGeofencingMode) => {
    dispatch({ field: 'outdoor-geofencing-mode', value });
    setOutdoorGeo(undefined);
  };

  const [checkinFreq, setCheckinFreq] = React.useState(state['indoor-checkin-frequency']);
  const handleIndoorCheckinChange = (value: any) => {
    dispatch({ field: 'indoor-checkin-frequency', value });
    setCheckinFreq(value);
  };

  React.useEffect(() => {
    if (state['indoor-checkin-frequency'] != null) {
      if (!checkinFrequencies.some((option) => option === state['indoor-checkin-frequency'])) {
        checkinFrequencies.push(state['indoor-checkin-frequency']);
        checkinFrequencies.sort((a, b) => a - b);
        setCheckinFreq(state['indoor-checkin-frequency']);
      }
    }
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-12 col-md-6">
          <Select
            value={checkinFreq}
            onChange={handleIndoorCheckinChange}
            label={t('profile-configuration.indoor-checkin-frequency.label')}
            options={checkinFrequencies.map((v) => ({ value: v, label: getCheckinLabel(v) }))}
          />
          <XsSpacer />
        </div>
        <div className="col-12 col-md-6">
          <Alert type={'message'}>{t('profile-configuration.checkin-frequency.explanation')}</Alert>
        </div>
      </div>
      <Spacer />
      <div className="row">
        <div className="col-12 col-md-6">
          <Select
            value={outdoorGeofencingMode}
            onChange={handleOutdoorGeofencingModeChange}
            label={t('profile-configuration.outdoor-geofencing-mode.label')}
            options={
              isProDevice
                ? Object.values(OutdoorGeofencingMode).map((v) => ({
                    value: v,
                    label: t(`profile-configuration.outdoor-geofencing-mode.${v}`),
                  }))
                : Object.values(OutdoorGeofencingMode)
                    .filter((mode) => mode !== OutdoorGeofencingMode.Geo)
                    .map((v) => ({ value: v, label: t(`profile-configuration.outdoor-geofencing-mode.${v}`) }))
            }
          />
          <XsSpacer />
          {state['outdoor-geofencing-mode'] === OutdoorGeofencingMode.Geo && props.siteUuid && (
            <OutdoorGeofenceInput
              siteUuid={props.siteUuid}
              value={outdoorGeo && Object.keys(outdoorGeo).length === 0 ? undefined : outdoorGeo}
              onChange={(value) => setOutdoorGeo(value)}
            />
          )}
        </div>
        <div className="col-12 col-md-6">
          <Alert type={'message'}>{t('profile-configuration.outdoor-geofencing-mode.explanation')}</Alert>
        </div>
      </div>
      <Spacer />
      <div className="row">
        <div className="col-12 col-md-6">
          <Switch
            onChange={(value) => dispatch({ field: 'indoor-geofencing-enabled', value })}
            checked={state['indoor-geofencing-enabled'] ?? false}
            label={t('profile-configuration.indoor-geofencing-enabled.label')}
          />
          <XsSpacer />
          {state['indoor-geofencing-enabled'] && props.siteUuid && (
            <GeofenceZonesInput
              siteUuid={props.siteUuid}
              selectedZones={state['indoor-geofencing-zones'] ?? []}
              onChange={(value) => dispatch({ field: 'indoor-geofencing-zones', value })}
            />
          )}
        </div>
        <div className="col-12 col-md-6">
          <Alert type={'message'}>{t('profile-configuration.indoor-geofencing.explanation')}</Alert>
        </div>
      </div>
      <Spacer />
      <SubmitButton loading={props.isSubmitting}>{t('global.update')}</SubmitButton>
    </form>
  );
}

const XsSpacer = styled.div`
  width: 100%;
  height: 10px;
`;

const Spacer = styled.div`
  width: 100%;
  height: 30px;
`;

import styled from 'styled-components';
import OutdoorGeofenceEditor from '~components/OutdoorGeofenceEditor';
import { useSite } from '~hooks/site';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';

const InputContainer = styled.div`
  width: 100%;
`;

const InputLabel = styled.label`
  font-family: Poppins;
  font-size: 14px;
  font-weight: 600;

  display: block;

  margin-bottom: 10px;

  color: var(--midnight-blue);
`;

const Value = styled.p`
  display: block;

  width: 100%;
  padding: 10px 15px;

  color: var(--comet);
  border: solid 1px #dee6f2;
  border-radius: 4px;
  background-color: var(--white);
  font-family: Poppins;
  cursor: pointer;

  &:hover {
    border: solid 1px var(--turquoise);
    box-shadow: 0 5px 20px 0 rgba(14, 12, 69, 0.1);
  }
`;

function GeofenceZonesInput({
  siteUuid,
  selectedZones,
  onChange,
}: {
  siteUuid: string;
  selectedZones: string[];
  onChange: (zones: string[]) => void;
}) {
  const allZones = useZones({ siteUuid });
  const mappedZones = React.useMemo(
    () => selectedZones.map((uuid) => allZones.find((z) => z.uuid === uuid)).filter((z): z is ZoneEntry => !!z),
    [selectedZones, allZones]
  );
  const [isEdit, setEdit] = React.useState(false);
  return (
    <InputContainer>
      <InputLabel>{t('profile-configuration.indoor-geofencing-zones.label')}</InputLabel>
      <Value onClick={() => setEdit(true)}>
        {mappedZones.length
          ? mappedZones.map((z) => z.name).join(', ')
          : t('profile-configuration.indoor-geofencing-zones.empty')}
      </Value>

      {isEdit && (
        <Modal onClose={() => setEdit(false)} style={{ maxWidth: 900 }}>
          <GeofenceCreatorContainer
            selectedZones={selectedZones}
            onSave={(zones) => {
              setEdit(false);
              onChange(zones);
            }}
            onCancel={() => setEdit(false)}
            siteUuid={siteUuid}
          />
        </Modal>
      )}
    </InputContainer>
  );
}

function OutdoorGeofenceInput({
  siteUuid,
  value,
  onChange,
}: {
  siteUuid: string;
  value?: GeoJSON.Feature<GeoJSON.Polygon>;
  onChange: (geo?: GeoJSON.Feature<GeoJSON.Polygon>) => void;
}) {
  const [isEdit, setEdit] = React.useState(false);
  const site = useSite(siteUuid);
  const { data: siteConfig } = useSiteConfiguration();
  const defaultGeo = siteConfig?.['default-outdoor-geofencing-geo'];
  return (
    <InputContainer>
      <InputLabel>{t('profile-configuration.outdoor-geofencing-geo.label')}</InputLabel>
      <Value onClick={() => setEdit(true)}>
        {value
          ? t('profile-configuration.outdoor-geofencing-geo.edit')
          : t('profile-configuration.outdoor-geofencing-geo.empty')}
      </Value>

      {isEdit && site && (
        <Modal onClose={() => setEdit(false)} style={{ maxWidth: 900 }}>
          <OutdoorGeofenceEditor
            residentSetting={true}
            onCancel={() => setEdit(false)}
            onSave={(x) => {
              setEdit(false);
              onChange(x);
            }}
            lat={site.lat}
            long={site.long}
            polygon={value}
            defaultPolygon={defaultGeo}
          />
        </Modal>
      )}
    </InputContainer>
  );
}
